import { ChevronDown } from 'lucide-react';
import { useState, type CSSProperties, type FC } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/** The masthead's navigation voice, worn by the menu and by its stand-in alike. */
const LUMERA_NAV = 'items-center gap-8 whitespace-nowrap text-[14px] tracking-[0.01em]';

/**
 * The rail's ground.
 *
 * The shared announcement rail inverts the theme's text and background. This
 * theme wants it in the primary cocoa rather than near-black, so - as Feast
 * does with its sand - the band redefines the variable the rail paints with,
 * and a merchant who repaints their primary still gets a rail that matches.
 */
const RAIL_STYLE = {
  '--storefront-palette-text': 'var(--storefront-palette-primary)',
} as CSSProperties;

/**
 * One item on the bar, and whatever the merchant filed under it.
 *
 * "Categories ▾" is how a beauty shop is navigated - skin, makeup, hair - so an
 * item with children opens them in a small rounded card. The item itself stays
 * a link: the shelves are a shortcut, never the only way through. Opens on
 * hover and on focus, closes on Escape.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const [open, setOpen] = useState(false);
  const children = item.items ?? [];

  if (children.length === 0) {
    return <MenuLink item={item} preview={preview} className="hover:opacity-60" />;
  }

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      <span className="flex items-center gap-1">
        <MenuLink item={item} preview={preview} className="hover:opacity-60" />
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open ? (
        // pt-3 rather than a margin, so the gap between the item and the card
        // is still inside the hover area and crossing it does not close it.
        <div className="absolute left-1/2 top-full z-40 -translate-x-1/2 pt-3">
          <div
            className="min-w-52 rounded-2xl py-2 shadow-xl"
            style={{ backgroundColor: 'var(--storefront-background)' }}
          >
            {children.map((child) => (
              <MenuLink
                key={`${child.label}:${child.url}`}
                item={child}
                preview={preview}
                className="block px-5 py-2 text-[14px] hover:opacity-60"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A floating masthead.
 *
 * A thin rail for the shop's standing promises, then the masthead itself: a
 * rounded, softly shadowed bar that sits a little way in from the page's edges
 * rather than spanning them, with the wordmark on the left, the menu in the
 * middle and the shopper's corner - search, wishlist, bag, account - on the
 * right. The bar floats on the page's own colour, so it reads the same over a
 * cream section as over a photograph scrolled beneath it.
 *
 * Like the other families, the bar stands the shop's own shelves in when the
 * merchant has built no menu, and below the large breakpoint the menu moves
 * into the sheet behind the menu button.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);

  return (
    <header
      className={cn(header.sticky && !preview && 'sticky top-0 z-40')}
      style={{ backgroundColor: 'var(--storefront-background)' }}
    >
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      <StorefrontContainer className="py-3">
        <div
          className="flex min-h-[4rem] items-center gap-5 rounded-full border px-5 shadow-[0_6px_24px_-12px_rgba(60,40,25,0.25)] sm:px-7"
          style={{
            backgroundColor: 'var(--storefront-background)',
            borderColor: 'var(--storefront-surface)',
          }}
        >
          <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryFallback />
          <StoreLogo
            storeName={storeName}
            logoUrl={logoUrl}
            logoWidthPx={header.logo_width_px}
            showStoreName={header.show_store_name}
            preview={preview}
            className="gap-2.5"
            nameClassName="text-[1.9rem] leading-none tracking-[0.02em] lowercase"
            nameStyle={{
              fontFamily: 'var(--storefront-heading-font)',
              fontWeight: 'var(--storefront-heading-weight)' as CSSProperties['fontWeight'],
              color: 'var(--storefront-text)',
            }}
          />

          <div className="hidden min-w-0 flex-1 justify-center lg:flex preview-narrow:hidden!">
            {items.length > 0 ? (
              <nav aria-label={menu?.name} className={cn('flex', LUMERA_NAV)}>
                {items.map((item) => (
                  <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />
                ))}
              </nav>
            ) : preview ? null : (
              <HeaderCategoryLinks
                className={cn('flex', LUMERA_NAV)}
                linkClassName="hover:opacity-60"
                header={header}
                limit={5}
              />
            )}
          </div>

          <div className="ms-auto flex shrink-0 items-center gap-4 sm:gap-5">{endSlot}</div>
        </div>
      </StorefrontContainer>
    </header>
  );
};
